import React, { useState, useEffect } from 'react';
import api from '../../../lib/api';

const hours = ['6AM', '8AM', '10AM', '12PM', '2PM', '4PM', '6PM', '8PM'];
const hourValues = [6, 8, 10, 12, 14, 16, 18, 20];
const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const getColor = (value) => {
  if (value >= 80) return '#0F766E';
  if (value >= 60) return '#0D9488';
  if (value >= 40) return '#14B8A6';
  if (value >= 20) return '#5EEAD4';
  if (value > 0) return '#CCFBF1';
  return '#F0FDF4';
};

const AttendanceHeatmap = () => {
  const [tooltip, setTooltip] = useState(null);
  const [heatmapData, setHeatmapData] = useState(Array(7)?.fill(null)?.map(() => Array(8)?.fill(0)));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        setLoading(true);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);

        const { data } = await api.get('/attendances', { params: { from: thirtyDaysAgo?.toISOString(), limit: 2000 } });

        if (!data || data?.length === 0) {
          setLoading(false);
          return;
        }

        // Build heatmap: rows = days (Mon=1..Sun=0), cols = hour buckets
        const grid = Array(7)?.fill(null)?.map(() => Array(8)?.fill(0));

        data?.forEach(record => {
          const checkIn = new Date(record?.check_in_time);
          // JS getDay: 0=Sun, 1=Mon...6=Sat → remap to Mon=0..Sun=6
          const jsDay = checkIn?.getDay();
          const dayIdx = jsDay === 0 ? 6 : jsDay - 1;
          const hour = checkIn?.getHours();

          // Find closest hour bucket
          let hourIdx = 0;
          let minDiff = Math.abs(hour - hourValues?.[0]);
          for (let i = 1; i < hourValues?.length; i++) {
            const diff = Math.abs(hour - hourValues?.[i]);
            if (diff < minDiff) { minDiff = diff; hourIdx = i; }
          }

          grid[dayIdx][hourIdx]++;
        });

        // Normalize to 0-100
        const maxVal = Math.max(...grid?.flat(), 1);
        const normalized = grid?.map(row => row?.map(v => Math.round((v / maxVal) * 100)));
        setHeatmapData(normalized);
      } catch (err) {
        console.error('Error fetching attendance heatmap:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, []);

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm lg:text-base font-semibold font-heading text-[var(--color-foreground)]">
          Attendance Pattern Heatmap
        </h3>
        <span className="text-xs text-[var(--color-muted-foreground)] font-caption">Last 30 days</span>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-xs text-[var(--color-muted-foreground)] font-caption">Loading attendance data...</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[280px]">
            <div className="flex gap-1 mb-1 ml-8">
              {hours?.map((h) => (
                <div key={h} className="flex-1 text-center text-xs text-[var(--color-muted-foreground)] font-caption">
                  {h}
                </div>
              ))}
            </div>
            <div className="space-y-1">
              {days?.map((day, di) => (
                <div key={day} className="flex items-center gap-1">
                  <span className="w-7 text-xs text-[var(--color-muted-foreground)] font-caption flex-shrink-0">
                    {day}
                  </span>
                  {heatmapData?.[di]?.map((val, hi) => (
                    <div
                      key={hi}
                      className="flex-1 h-7 rounded-sm cursor-pointer transition-transform hover:scale-110 relative"
                      style={{ backgroundColor: getColor(val) }}
                      onMouseEnter={() => setTooltip({ day, hour: hours?.[hi], val })}
                      onMouseLeave={() => setTooltip(null)}
                    />
                  ))}
                </div>
              ))}
            </div>
            {tooltip && (
              <div className="mt-2 p-2 bg-[var(--color-muted)] rounded-lg text-xs font-caption text-[var(--color-foreground)]">
                <strong>{tooltip?.day} {tooltip?.hour}:</strong> {tooltip?.val}% activity
              </div>
            )}
            <div className="flex items-center gap-2 mt-3">
              <span className="text-xs text-[var(--color-muted-foreground)] font-caption">Low</span>
              {['#CCFBF1', '#5EEAD4', '#14B8A6', '#0D9488', '#0F766E']?.map((c) => (
                <div key={c} className="w-5 h-3 rounded-sm" style={{ backgroundColor: c }} />
              ))}
              <span className="text-xs text-[var(--color-muted-foreground)] font-caption">High</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceHeatmap;